import { Check, Clock, MapPin, Users, Star, Phone, Shield, Sparkles, Calendar } from 'lucide-react';

/**
 * Parses a plain-text FAQ answer (from services data) and renders it with
 * rich formatting: intro paragraph + bullet list extracted from the text.
 */
export const ServiceFAQRichAnswer = ({ answer }: { answer: string }) => {
  // Split on sentence boundaries to create structure
  const sentences = answer.split(/(?<=\.)\s+/).filter(Boolean);
  
  // If the answer contains enumeration patterns (commas listing items), extract them
  const hasEnumeration = answer.includes(', ') && sentences.length >= 2;
  
  if (hasEnumeration && sentences.length >= 2) {
    // First sentence as intro
    const intro = sentences[0];
    // Look for sentence with lists (contains multiple commas)
    const listSentence = sentences.find(s => (s.match(/,/g) || []).length >= 2);
    const remaining = sentences.filter(s => s !== intro && s !== listSentence).join(' ');
    
    if (listSentence) {
      // Extract items from comma-separated or ":" separated content
      const colonIdx = listSentence.indexOf(':');
      let items: string[];
      let listIntro: string | null = null;
      
      if (colonIdx > -1) {
        listIntro = listSentence.slice(0, colonIdx).trim();
        items = listSentence.slice(colonIdx + 1).split(/,\s*|(?:\set\s)/).map(s => s.replace(/\.$/, '').trim()).filter(Boolean);
      } else {
        items = listSentence.split(/,\s*/).map(s => s.replace(/\.$/, '').trim()).filter(s => s.length > 3);
      }
      
      if (items.length >= 3) {
        return (
          <div className="space-y-3">
            <p>{intro}</p>
            {listIntro && <p className="text-foreground font-medium text-xs">{listIntro} :</p>}
            <ul className="space-y-1.5">
              {items.slice(0, 6).map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={13} className="text-primary mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {remaining && <p className="text-xs mt-2 text-foreground/70 italic">{remaining}</p>}
          </div>
        );
      }
    }
  }
  
  // Fallback: just render with paragraph breaks for long answers
  if (sentences.length >= 3) {
    return (
      <div className="space-y-2.5">
        <p className="font-medium text-foreground/80">{sentences[0]}</p>
        <p>{sentences.slice(1).join(' ')}</p>
      </div>
    );
  }
  
  return <p>{answer}</p>;
};

/**
 * Parses a plain-text city FAQ answer and renders with rich formatting.
 */
export const CityFAQRichAnswer = ({ answer }: { answer: string }) => {
  return <ServiceFAQRichAnswer answer={answer} />;
};
